/**
 * useFontScale — the OS font scale, clamped to the app's per-role cap, plus an
 * `isLargeText` flag for layouts that must change shape (label/value rows that
 * stack, side-by-side buttons that become a column).
 *
 * Reads `useWindowDimensions`, so it re-renders when Android changes the font
 * scale at runtime. On web `fontScale` is always 1 and `isLargeText` is false.
 *
 * @example
 * const { isLargeText } = useFontScale();
 * <View style={isLargeText ? styles.column : styles.row}>
 */
import { useWindowDimensions } from 'react-native';

import {
  FONT_SCALE_CAP,
  LARGE_TEXT_THRESHOLD,
  type TextScale,
} from '@/components/common/text';

export interface FontScaleInfo {
  /** OS font scale clamped to the cap for `scale`. */
  fontScale: number;
  /** OS font scale as reported, unclamped. */
  rawFontScale: number;
  /** True at or above LARGE_TEXT_THRESHOLD (1.3). */
  isLargeText: boolean;
}

export function useFontScale(scale: TextScale = 'chrome'): FontScaleInfo {
  const { fontScale } = useWindowDimensions();
  return {
    fontScale: Math.min(fontScale, FONT_SCALE_CAP[scale]),
    rawFontScale: fontScale,
    isLargeText: fontScale >= LARGE_TEXT_THRESHOLD,
  };
}
